import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import Loader from '../../../Components/Ui/Loader';

const Brandfilter = ({
  searchQuery,
  setSearchQuery,
  categoryFilter,
  setCategoryFilter,
  categories = [],
  brandFilter,
  setBrandFilter,
  brands = [],
  statusOptions = [],
  isLoading = false,
  user, // Receive the user object
}) => {
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const inputRef = useRef(null);
  const searchInitiated = useRef(false);
  const prevIsLoading = useRef(isLoading);

  const uniqueBrands = useMemo(() => {
    return [...new Map(brands.filter(Boolean).map((b) => [b?._id, b])).values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [brands]);

  const uniqueCategories = useMemo(() => {
    return [...new Map(categories.filter(Boolean).map((c) => [c?._id, c])).values()].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [categories]);

  const isBrandAndSeller = user?.role?.includes('brand') && user?.role?.includes('seller');

  // Sync local search with external query
  useEffect(() => {
    setLocalSearch(searchQuery);
  }, [searchQuery]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== localSearch) {
        searchInitiated.current = true;
        setSearchQuery(localSearch);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [localSearch, searchQuery, setSearchQuery]);

  // Restore focus after loading
  useEffect(() => {
    if (prevIsLoading.current && !isLoading && searchInitiated.current) {
      inputRef.current?.focus();
      searchInitiated.current = false;
    }
    prevIsLoading.current = isLoading;
  }, [isLoading]);

  const clearSearch = () => {
    setLocalSearch('');
    setSearchQuery('');
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
      {/* Search Input */}
      <div className="relative w-full sm:w-72">
        <Search className="absolute left-3 top-2.5 h-4 w-4 tertiary-text-color" />
        <input
          ref={inputRef}
          type="text"
          placeholder="Search"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-9 pr-8 rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-secondary hover:bg-gray-50 focus:outline-none focus:ring-2 border-color w-full"
          disabled={isLoading}
        />
        {localSearch && (
          <X
            className="absolute right-3 top-2.5 h-4 w-4 tertiary-text-color cursor-pointer hover:text-secondary"
            onClick={clearSearch}
          />
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
        {isLoading ? (
          <div className="w-32 flex items-center justify-center">
            <Loader />
          </div>
        ) : (
          <>
            {!isBrandAndSeller && (
              <select
                className="border border-gray-300 rounded-lg text-sm border-color focus:border-[#832474] focus:outline-none focus:ring-2 p-2 w-full sm:w-auto"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
                disabled={isLoading}
              >
                <option value="">All Brands</option>
                {uniqueBrands.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            )}

            <select
              className="border border-gray-300 rounded-lg text-sm border-color focus:border-[#832474] focus:outline-none focus:ring-2 p-2 w-full sm:w-auto"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              disabled={isLoading}
            >
              <option value="">All Categories</option>
              {uniqueCategories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </>
        )}
      </div>
    </div>
  );
};

export default React.memo(Brandfilter);
